import { h } from "preact";
import { useState, useEffect, useCallback } from "preact/hooks";
import ViralReferral from "./ViralReferral";
import SimpleReferral from "./SimpleReferral";
import { traceWidgetLoad } from "../internal_utilities/utilities";

const ThankPageWidgets = ({ dataPosition }) => {
  const [language, setLanguage] = useState("en");
  const [renderConfig, setRenderConfig] = useState(null);
  // This boolean is used to check whether the user comes under the billing logic and whether the widget is allowed to render
  const [billingFlag, setBillingFlag] = useState(false);
  const [isCampaignBlocked, setIsCampaignBlocked] = useState(true);
  const [currentCampaign, setCurrentCampaign] = useState(null);

  const getCurrentLangugeObject = selectedLanguage => {
    let langConfig = renderConfig.config.widgetConfig.langConfig.offerWidget;
    let defaultLanguage =
      renderConfig.config.widgetConfig.langConfig.defaultLanguage;

    if (renderConfig && langConfig.hasOwnProperty(language))
      return langConfig[selectedLanguage];
    else return langConfig[defaultLanguage];
  };

  /**
   * Initialising current campaign to render
   * @function
   * @param {object} configJson - config obtaimed from corner DB
   */
  const initCurrentCampaign = async configJson => {
    // We have the following defenitions becuase if there are no campaigns in viral or simple referrals teh db gives us a null value. and If we try to concat null value it returns an error
    let simpleReferralsArray = configJson.config.referral.simpleReferrals
      ? configJson.config.referral.simpleReferrals
      : [];
    let viralReferralsArray = configJson.config.referral.viralReferrals
      ? configJson.config.referral.viralReferrals
      : [];

    // Combining simple & viral referrals
    let combinedCampaigns = simpleReferralsArray.concat(viralReferralsArray);

    // Sorting the campaigns based on priority
    let sortedCampaigns = combinedCampaigns.sort(
      (a, b) => b.priority - a.priority
    );
    const campaignToRender = sortedCampaigns.find(campaignObj => {
      // minOrderValue lies in different places in the JSON Structure of both viral and simple referral, that is the reason why we have the following if conditions
      if (campaignObj.type === "SIMPLE_REFERRAL") {
        return (
          parseInt(window.Shopify.checkout.total_price, 10) >=
          campaignObj.minOrderValue
        );
      } else if (campaignObj.type === "VIRAL_REFERRAL") {
        return (
          parseInt(window.Shopify.checkout.total_price, 10) >=
          campaignObj.thankyouPageConfig.minOrderValue
        );
      }
    });
    checkForBlockedCollections(campaignToRender);
    setCurrentCampaign(campaignToRender);
    traceWidgetLoad({
      name: "widget_loaded",
      domain: window.Shopify.shop,
      url: window.location.href,
      referrer: document.referrer || "",
      key: ["app_name", "widget_type"],
      value: ["offerscorner", campaignToRender.type],
      operatingSystem: window.navigator.userAgentData
        ? window.navigator.userAgentData.platform
        : window.navigator.platform,
      screenSize: window.screen
        ? `${window.screen.width}-${window.screen.height}`
        : "",
      browserVersion: window.navigator.userAgentData
        ? window.navigator.userAgentData.brands[0].version
        : "",
      browser: window.navigator.userAgentData
        ? window.navigator.userAgentData.brands[0].brand
        : ""
    });
  };

  /**
   * The fuction which initialises the value of the isCampaignBlocked state.
   * @function
   * @param {object} campaignToRender - campaign of which where it is supposed to check for blocked products
   */
  const checkForBlockedCollections = async campaignToRender => {
    if (
      campaignToRender.blockedCollections &&
      campaignToRender.blockedCollections.productArray
    ) {
      let blockedProductsIDs = campaignToRender.blockedCollections.productArray;

      let cartProductIdArray = window.Shopify.checkout.line_items.map(
        product => product.product_id
      );
      const isAnyCartProductInBlockedProductIds = blockedProductsIDs.some(
        productId => cartProductIdArray.includes(productId)
      );

      setIsCampaignBlocked(isAnyCartProductInBlockedProductIds);
    } else {
      setIsCampaignBlocked(false);
    }
  };

  const handlePreviewConfigChange = useCallback(event => {
    const { data } = event;
    const previewConfig = JSON.parse(data);
    setRenderConfig(previewConfig);
    initCurrentCampaign(previewConfig);
    setLanguage(previewConfig.config.widgetConfig.langConfig.defaultLanguage);
  }, []);

  // This event listner is for preview
  useEffect(
    () => {
      if (window.isPreviewPage === true) {
        setBillingFlag(true);
        console.log(
          "%c Warning:  Corner Preview Mode (v1.0) Loaded ",
          "background: #ffc400; color: #1c1603; padding:4px; font-weight:bolder; border-radius:4px"
        );
        window.addEventListener("message", handlePreviewConfigChange);
        return () => {
          window.removeEventListener("message", handlePreviewConfigChange);
        };
      }
    },
    [handlePreviewConfigChange]
  );

  useEffect(async () => {
    // The task of setting values inside window.ofcoConfig is done in index.js right at the beginning of render
    if (window.ofcoConfig && !window.isPreviewPage) {
      setRenderConfig(window.ofcoConfig);
      if (
        window.ofcoConfig.config.referral.usageStats.billingType ===
          "usage_charge" ||
        window.ofcoConfig.config.referral.usageStats
          .isFreeReferralSalesExceeded !== true
      ) {
        setBillingFlag(true);
      }
      initCurrentCampaign(window.ofcoConfig);
      setLanguage(window.Shopify.locale);
      console.log(
        "%c Referral Offers powered by Corner ",
        "background: #222; color: #bada55"
      );
    }
  }, []);

  const renderWidget = widgetType => {
    switch (widgetType) {
      case "VIRAL_REFERRAL": {
        return (
          <ViralReferral
            renderConfig={renderConfig}
            currentLanguage={getCurrentLangugeObject(language)}
            dataPosition={parseInt(dataPosition, 10)}
            currentCampaign={currentCampaign}
          />
        );
      }
      case "SIMPLE_REFERRAL": {
        return (
          <SimpleReferral
            renderConfig={renderConfig}
            currentLanguage={getCurrentLangugeObject(language)}
            dataPosition={parseInt(dataPosition, 10)}
            currentCampaign={currentCampaign}
          />
        );
      }
      default: {
        console.error(
          "Offers Corner <Midas>: Invalid Widget Type received -->",
          widgetType
        );
        break;
      }
    }
  };

  if (renderConfig && currentCampaign && billingFlag && !isCampaignBlocked)
    return renderWidget(currentCampaign.type);
};

export default ThankPageWidgets;
